import { Heart, Users, Target, Clock, Award, Globe, Shield, BookOpen, ChevronRight } from 'lucide-react';
import ProgramDetail from '@/components/ProgramDetail';
import { useLanguage } from '@/components/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Health = () => {
  const { t } = useLanguage();
  
  const healthDetails = [
    t('programs.health.details.background', 'The Health program was established by Vision Jeunesse Nouvelle in 2023. The idea to start this program came after identifying various health challenges faced by youth who lacked access to sufficient information and support services.'),
    t('programs.health.details.recreation', 'VJN also recognized that many youth participating in recreational activities could face accidents requiring basic first aid. Additionally, many sports activities at VJN require medical staff to treat athletes.'),
    t('programs.health.details.youngMothers', 'The program also addresses the needs of young mothers who lack sufficient knowledge about reproductive health and childcare. While various health services are provided to youth, additional services are needed to continue supporting youth health.'),
    t('programs.health.details.partners', 'VJN has helped youth access various health services through training funded by Imbuto Foundation in the Baho neza, Mountain Movers, and Door to Door Campaign projects. The organization also supports young mothers and their parents through savings and credit groups with MISEREOR and Imbuto Foundation.')
  ];

  const objectives = [
    t('programs.health.objectives.main', 'To improve the well-being of partners through various trainings, help them access sufficient health information, and provide various services to minimize the effects of poor health.'),
    t('programs.health.objectives.specific1', 'Provide training to enhance youth knowledge about health-related matters.'),
    t('programs.health.objectives.specific2', 'Help youth access basic health services and provide counseling to help them maintain good health.'),
    t('programs.health.objectives.specific3', 'Advocate for youth in various institutions.'),
    t('programs.health.objectives.specific4', 'Connect parents and children from families with conflicts related to young mothers.'),
    t('programs.health.objectives.specific5', 'Help youth reduce the effects of poor health.')
  ];

  const targetGroups = {
    direct: [
      t('programs.health.target.direct.youth', 'Youth'),
      t('programs.health.target.direct.adults', 'Adults'),
      t('programs.health.target.direct.disabled', 'People with disabilities')
    ],
    indirect: [
      t('programs.health.target.indirect.authorities', 'Government authorities'),
      t('programs.health.target.indirect.health', 'Health institutions'),
      t('programs.health.target.indirect.ngos', 'NGOs')
    ]
  };

  const services = [
    {
      title: t('programs.health.services.training.title', 'Training Programs'),
      items: [
        { name: 'PAC', duration: '3 days' },
        { name: 'Basic First Aid', duration: '15 days' },
        { name: 'Fight Against Abuse', duration: '3 days' },
        { name: 'Trauma Healing', duration: '9 days' },
        { name: 'Mental Health Training', duration: '9 days' },
        { name: 'Life Skills', duration: '9 days' },
        { name: 'Reproductive Health', duration: '3 days' },
        { name: 'Community Therapy', duration: '5 days' }
      ]
    },
    {
      title: t('programs.health.services.support.title', 'Support Services'),
      items: [
        { name: t('programs.health.services.support.mental', 'Mental Health Services for Young Mothers'), duration: 'Ongoing' },
        { name: t('programs.health.services.support.firstAid', 'Basic First Aid Services'), duration: 'Ongoing' },
        { name: t('programs.health.services.support.mobile', 'Mobile GBV Clinic Support'), duration: '1 day' },
        { name: t('programs.health.services.support.psychosocial', 'Psychosocial Support'), duration: 'Ongoing' }
      ]
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="relative mb-16 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-red-800/90 z-10" />
        <div className="relative z-20 p-12 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block p-3 bg-red-600/30 rounded-full mb-6">
              <Heart className="h-8 w-8" />
            </div>
            <h1 className="text-5xl font-bold mb-6">
              {t('programs.health.title', 'Health Program')}
            </h1>
            <p className="text-xl mb-8 text-red-100 leading-relaxed">
              {t('programs.health.intro.description', 'Our Health Program aims to improve youth well-being through comprehensive health education, access to basic health services, and support for young mothers. We work to ensure that young people have the knowledge and resources they need to maintain good health and make informed decisions.')}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Users className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">65%</div>
                  <div className="text-sm text-red-100">Youth Population</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Globe className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">9+</div>
                  <div className="text-sm text-red-100">Training Programs</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Heart className="h-6 w-6" />
                <div>
                  <div className="text-2xl font-bold">4+</div>
                  <div className="text-sm text-red-100">Support Services</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="bg-gradient-to-br from-red-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="md:w-1/3">
            <div className="bg-white p-8 rounded-xl shadow-md border border-red-100">
              <div className="text-red-600 mb-6">
                <Heart size={48} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                {t('programs.health.title', 'Health Program')}
              </h2>
              <h3 className="text-xl font-semibold text-red-600 mb-4">
                {t('programs.health.subtitle', 'Promoting Youth Well-being')}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {t('programs.health.description', 'Our program addresses youth health challenges by providing comprehensive health education, access to basic health services, and support for young mothers. We work to ensure that young people have the knowledge and resources they need to maintain good health.')}
              </p>
            </div>
          </div>
          
          <div className="md:w-2/3">
            <div className="bg-white rounded-xl shadow-md p-8 border border-red-100">
              <h4 className="text-2xl font-semibold text-gray-900 mb-6">
                {t('programs.health.details.title', 'Program Details')}
              </h4>
              <div className="space-y-6">
                {healthDetails.map((detail, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-colors">
                      <span className="text-red-600 font-semibold group-hover:text-white">{index + 1}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="bg-gradient-to-br from-red-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Target className="h-8 w-8 mr-3 text-red-600" />
          {t('programs.health.objectives.title', 'Program Objectives')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {objectives.map((objective, index) => (
            <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all border border-red-100 group">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center group-hover:bg-red-600 transition-colors">
                <Award className="h-6 w-6 text-red-600 group-hover:text-white" />
              </div>
              <p className="text-gray-700 leading-relaxed">{objective}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Target Groups Section */}
      <section className="bg-gradient-to-br from-red-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Users className="h-8 w-8 mr-3 text-red-600" />
          {t('programs.health.target.title', 'Target Groups')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl p-8 shadow-md border border-red-100">
            <h3 className="text-2xl font-semibold text-red-600 mb-6">
              {t('programs.health.target.direct.title', 'Direct Beneficiaries')}
            </h3>
            <ul className="space-y-4">
              {targetGroups.direct.map((group, index) => (
                <li key={index} className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center group-hover:bg-red-600 transition-colors">
                    <Clock className="h-5 w-5 text-red-600 group-hover:text-white" />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{group}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-xl p-8 shadow-md border border-red-100">
            <h3 className="text-2xl font-semibold text-red-600 mb-6">
              {t('programs.health.target.indirect.title', 'Indirect Beneficiaries')}
            </h3>
            <ul className="space-y-4">
              {targetGroups.indirect.map((group, index) => (
                <li key={index} className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center group-hover:bg-red-600 transition-colors">
                    <Clock className="h-5 w-5 text-red-600 group-hover:text-white" />
                  </div>
                  <span className="text-gray-700 leading-relaxed">{group}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-gradient-to-br from-red-50 to-white rounded-2xl shadow-xl p-8 mb-16">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Shield className="h-8 w-8 mr-3 text-red-600" />
          {t('programs.health.services.title', 'Our Services')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-xl p-8 shadow-md border border-red-100">
              <h3 className="text-2xl font-semibold text-red-600 mb-6">{service.title}</h3>
              <div className="space-y-4">
                {service.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center justify-between p-4 bg-red-50 rounded-lg group hover:bg-red-100 transition-colors">
                    <span className="text-gray-700 font-medium">{item.name}</span>
                    <span className="text-red-600 text-sm font-medium">{item.duration}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl shadow-xl p-12">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-6 text-white">
            {t('programs.health.cta.title', 'Join Our Health Program')}
          </h2>
          <p className="text-xl text-red-100 mb-10 leading-relaxed">
            {t('programs.health.cta.description', 'Be part of our mission to improve youth health and well-being. Your involvement can make a difference in the lives of young people.')}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/volunteer">
              <Button className="bg-white text-red-600 hover:bg-red-50 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                {t('programs.health.cta.volunteer', 'Volunteer With Us')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-red-700 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                {t('programs.health.cta.donate', 'Support Our Programs')}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Health;
